import React from 'react';
import { Shield, Lock, UserCheck, Clock, Award, Star, CheckCircle, Heart, Zap, Globe } from 'lucide-react';
import './TrustSection.css';

const TrustSection = () => {
  const trustFeatures = [
    {
      icon: Shield,
      title: 'HIPAA Compliant',
      description: 'Your data is protected and secure',
      color: '#10B981',
      bgColor: 'rgba(16, 185, 129, 0.1)'
    },
    {
      icon: Lock,
      title: 'SSL Secured',
      description: '256-bit encryption for all data',
      color: '#3B82F6',
      bgColor: 'rgba(59, 130, 246, 0.1)'
    },
    {
      icon: UserCheck,
      title: 'Verified Doctors',
      description: 'All doctors are licensed and verified',
      color: '#F59E0B',
      bgColor: 'rgba(245, 158, 11, 0.1)'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock customer support',
      color: '#8B5CF6',
      bgColor: 'rgba(139, 92, 246, 0.1)'
    },
    {
      icon: Award,
      title: 'Quality Assured',
      description: 'Rigorous quality standards',
      color: '#EF4444',
      bgColor: 'rgba(239, 68, 68, 0.1)'
    },
    {
      icon: Star,
      title: 'Patient Rated',
      description: 'Highly rated by patients',
      color: '#F97316',
      bgColor: 'rgba(249, 115, 22, 0.1)'
    }
  ];

  const trustStats = [
    {
      number: '99.9%',
      label: 'Uptime',
      icon: Zap,
      color: '#10B981'
    },
    {
      number: '4.8/5',
      label: 'Patient Rating',
      icon: Star,
      color: '#F59E0B'
    },
    {
      number: '50K+',
      label: 'Happy Patients',
      icon: Heart,
      color: '#EF4444'
    },
    {
      number: '24/7',
      label: 'Support',
      icon: Globe,
      color: '#3B82F6'
    }
  ];

  const testimonials = [
    {
      text: "DoctDash made finding the right doctor so easy. The platform is intuitive and the doctors are top-notch.",
      author: "Sarah Johnson",
      role: "Patient",
      rating: 5
    },
    {
      text: "As a healthcare provider, I appreciate how DoctDash connects me with patients who truly need my expertise.",
      author: "Dr. Michael Chen",
      role: "Cardiologist",
      rating: 5
    },
    {
      text: "The 24/7 support and security features give me complete peace of mind when using this platform.",
      author: "Emily Rodriguez",
      role: "Patient",
      rating: 5
    }
  ];

  return (
    <section className="trust-section">
      <div className="container">
        {/* Header */}
        <div className="trust-header">
          <div className="trust-badge">
            <Shield size={16} />
            <span>Trusted & Secure</span>
          </div>
          <h2>Trusted by Patients Nationwide</h2>
          <p>Your health and privacy are our top priorities</p>
        </div>

        {/* Trust Features */}
        <div className="trust-features">
          {trustFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index} 
                className="trust-feature"
                style={{ '--feature-color': feature.color, '--feature-bg': feature.bgColor }}
              >
                <div className="feature-icon" style={{ backgroundColor: feature.bgColor, color: feature.color }}>
                  <IconComponent size={24} />
                </div>
                <div className="feature-content">
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
                <CheckCircle size={20} className="feature-check" style={{ color: feature.color }} />
              </div>
            );
          })}
        </div>

        {/* Trust Stats */}
        <div className="trust-stats">
          <div className="stats-grid">
            {trustStats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="trust-stat">
                  <div className="stat-icon" style={{ color: stat.color }}>
                    <IconComponent size={32} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-number" style={{ color: stat.color }}>
                      {stat.number}
                    </div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        <div className="testimonials">
          <div className="testimonials-header">
            <h3>What Our Patients Say</h3>
            <p>Real experiences from real people</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="star filled" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-info">
                    <div className="author-name">{testimonial.author}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;